import { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { AdminSidebar } from '../../components/AdminSidebar';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Plus, Trash2, Type, ExternalLink, Upload, Globe, Search } from 'lucide-react';

import { API_URL } from '../../utils/api';
import { getErrorMessage } from '../../utils/errors';
import { resolveFontUrl } from '../../utils/fonts';

export default function AdminFonts() {
  const [fonts, setFonts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [uploadTab, setUploadTab] = useState('google');
  const [formData, setFormData] = useState({ name: '', font_family: '', font_url: '' });
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [search, setSearch] = useState('');

  const fontType = (font) => font.id.startsWith('default-') ? 'Default' : font.font_url?.includes('googleapis') ? 'Google' : 'Custom';
  const q = search.trim().toLowerCase();
  const visibleFonts = q
    ? fonts.filter(f => [f.name, f.font_family, fontType(f)].some(v => String(v || '').toLowerCase().includes(q)))
    : fonts;

  useEffect(() => {
    fetchFonts();
  }, []);

  const fetchFonts = async () => {
    try {
      const response = await axios.get(`${API_URL}/fonts`);
      setFonts(response.data);
      // Load font stylesheets
      response.data.forEach(font => {
        if (font.font_url && !document.querySelector(`link[href="${font.font_url}"]`)) {
          const link = document.createElement('link');
          link.href = resolveFontUrl(font.font_url);
          link.rel = 'stylesheet';
          document.head.appendChild(link);
        }
      });
    } catch (error) {
      console.error('Failed to fetch fonts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleFontSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/admin/fonts`, formData);
      toast.success('Font added successfully');
      fetchFonts();
      setDialogOpen(false);
      setFormData({ name: '', font_family: '', font_url: '' });
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to add font'));
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedExtensions = ['ttf', 'otf', 'woff', 'woff2'];
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (!allowedExtensions.includes(ext)) {
      toast.error('Invalid file type. Allowed: TTF, OTF, WOFF, WOFF2');
      return;
    }

    setUploading(true);
    const uploadFormData = new FormData();
    uploadFormData.append('file', file);
    uploadFormData.append('font_name', formData.name || file.name.replace(/\.[^/.]+$/, ''));
    uploadFormData.append('font_family', formData.font_family || file.name.replace(/\.[^/.]+$/, ''));

    try {
      const response = await axios.post(
        `${API_URL}/upload/font?font_name=${encodeURIComponent(formData.name || file.name.replace(/\.[^/.]+$/, ''))}&font_family=${encodeURIComponent(formData.font_family || file.name.replace(/\.[^/.]+$/, ''))}`,
        uploadFormData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success('Font uploaded successfully');
      fetchFonts();
      setDialogOpen(false);
      setFormData({ name: '', font_family: '', font_url: '' });
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to upload font'));
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDelete = async (id) => {
    if (id.startsWith('default-')) {
      toast.error('Cannot delete default fonts');
      return;
    }
    if (!window.confirm('Are you sure you want to delete this font?')) return;
    try {
      await axios.delete(`${API_URL}/admin/fonts/${id}`);
      toast.success('Font deleted');
      fetchFonts();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete font'));
    }
  };

  return (
    <div className="min-h-screen bg-paper">
      <AdminSidebar />
      <div className="ml-64 p-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex flex-wrap gap-4 justify-between items-center mb-8">
            <div>
              <h1 className="font-serif text-3xl font-bold text-ink mb-2">Fonts</h1>
              <p className="text-ink/60">Manage available fonts for templates</p>
            </div>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-ink text-paper hover:bg-ink/90" data-testid="add-font-btn">
                  <Plus className="h-4 w-4 mr-2" />Add Font
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Add New Font</DialogTitle>
                </DialogHeader>
                
                <Tabs value={uploadTab} onValueChange={setUploadTab}>
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="google" className="flex items-center gap-2">
                      <Globe className="h-4 w-4" />
                      Google Fonts
                    </TabsTrigger>
                    <TabsTrigger value="upload" className="flex items-center gap-2">
                      <Upload className="h-4 w-4" />
                      Upload File
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="google">
                    <form onSubmit={handleGoogleFontSubmit} className="space-y-4 mt-4">
                      <div>
                        <Label>Display Name</Label>
                        <Input
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          placeholder="e.g., Elegant Script"
                          required
                          className="mt-2"
                          data-testid="font-name-input"
                        />
                      </div>
                      <div>
                        <Label>Font Family (CSS)</Label>
                        <Input
                          value={formData.font_family}
                          onChange={(e) => setFormData({ ...formData, font_family: e.target.value })}
                          placeholder="e.g., 'Elegant Script', cursive"
                          required
                          className="mt-2"
                          data-testid="font-family-input"
                        />
                      </div>
                      <div>
                        <Label>Google Fonts URL</Label>
                        <Input
                          value={formData.font_url}
                          onChange={(e) => setFormData({ ...formData, font_url: e.target.value })}
                          placeholder="https://fonts.googleapis.com/css2?family=..."
                          required
                          className="mt-2"
                          data-testid="font-url-input"
                        />
                        <p className="text-xs text-ink/50 mt-1">
                          Get the URL from{' '}
                          <a href="https://fonts.google.com" target="_blank" rel="noopener noreferrer" className="text-gold hover:underline">
                            Google Fonts <ExternalLink className="h-3 w-3 inline" />
                          </a>
                        </p>
                      </div>
                      <Button type="submit" className="w-full bg-ink text-paper hover:bg-ink/90" data-testid="save-font-btn">
                        Add Font
                      </Button>
                    </form>
                  </TabsContent>

                  <TabsContent value="upload">
                    <div className="space-y-4 mt-4">
                      <div>
                        <Label>Display Name</Label>
                        <Input
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          placeholder="e.g., My Custom Font"
                          className="mt-2"
                        />
                      </div>
                      <div>
                        <Label>Font Family (CSS)</Label>
                        <Input
                          value={formData.font_family}
                          onChange={(e) => setFormData({ ...formData, font_family: e.target.value })}
                          placeholder="e.g., MyCustomFont"
                          className="mt-2"
                        />
                        <p className="text-xs text-ink/50 mt-1">
                          This is the name you'll use in CSS. No spaces recommended.
                        </p>
                      </div>
                      <div>
                        <Label>Font File</Label>
                        <div className="mt-2 border-2 border-dashed border-border rounded-lg p-6 text-center">
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept=".ttf,.otf,.woff,.woff2"
                            onChange={handleFileUpload}
                            className="hidden"
                            id="font-file-input"
                          />
                          <label htmlFor="font-file-input" className="cursor-pointer">
                            <Upload className="h-8 w-8 mx-auto text-ink/40 mb-2" />
                            <p className="text-sm text-ink/60">
                              {uploading ? 'Uploading...' : 'Click to upload font file'}
                            </p>
                            <p className="text-xs text-ink/40 mt-1">
                              TTF, OTF, WOFF, WOFF2 (max 5MB)
                            </p>
                          </label>
                        </div>
                      </div>
                    </div>
                  </TabsContent>
                </Tabs>
              </DialogContent>
            </Dialog>
          </div>

          {loading ? (
            <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold"></div></div>
          ) : (
            <>
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <div className="relative w-full sm:w-72">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink/40" aria-hidden="true" />
                <Input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search name, family or type"
                  aria-label="Search fonts"
                  className="pl-9 bg-white"
                  data-testid="font-search"
                />
              </div>
              <span className="text-sm text-ink/60 ml-auto" aria-live="polite">
                {visibleFonts.length === fonts.length ? `${fonts.length} fonts` : `${visibleFonts.length} of ${fonts.length} fonts`}
              </span>
            </div>
            <div className="bg-white rounded-lg border border-border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Preview</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Font Family</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visibleFonts.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-ink/60 py-10">
                        {fonts.length === 0 ? 'No fonts yet.' : `No fonts match “${search.trim()}”.`}
                      </TableCell>
                    </TableRow>
                  )}
                  {visibleFonts.map((font) => (
                    <TableRow key={font.id}>
                      <TableCell>
                        <span style={{ fontFamily: font.font_family }} className="text-xl">
                          Aa Bb Cc
                        </span>
                      </TableCell>
                      <TableCell className="font-medium">{font.name}</TableCell>
                      <TableCell className="text-ink/60 font-mono text-sm">{font.font_family}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          font.id.startsWith('default-') ? 'bg-blue-100 text-blue-700' : 
                          font.font_url?.includes('googleapis') ? 'bg-green-100 text-green-700' :
                          'bg-purple-100 text-purple-700'
                        }`}>
                          {fontType(font)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        {!font.id.startsWith('default-') && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-destructive"
                            onClick={() => handleDelete(font.id)}
                            data-testid={`delete-font-${font.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            </>
          )}

          {/* Font Installation Guide */}
          <div className="mt-8 p-6 bg-cream rounded-lg">
            <h3 className="font-serif text-lg font-semibold mb-4 flex items-center gap-2">
              <Type className="h-5 w-5" />
              How to Add Fonts
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <Globe className="h-4 w-4" /> Google Fonts
                </h4>
                <ol className="list-decimal list-inside space-y-1 text-sm text-ink/70">
                  <li>Go to <a href="https://fonts.google.com" target="_blank" rel="noopener noreferrer" className="text-gold hover:underline">Google Fonts</a></li>
                  <li>Select a font and click "Get embed code"</li>
                  <li>Copy the URL from the &lt;link&gt; tag</li>
                  <li>Paste it in the form above</li>
                </ol>
              </div>
              <div>
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <Upload className="h-4 w-4" /> Custom Fonts
                </h4>
                <ol className="list-decimal list-inside space-y-1 text-sm text-ink/70">
                  <li>Prepare your font file (TTF, OTF, WOFF, WOFF2)</li>
                  <li>Click "Upload File" tab</li>
                  <li>Enter a display name and CSS family name</li>
                  <li>Upload the font file</li>
                </ol>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
